import React, { useState, useEffect, lazy, Suspense } from "react";
import { collection, getDocs, query, where, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { motion } from "framer-motion";
import { useAuth } from "../firebase/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import toast, { Toaster } from "react-hot-toast";

// Lazy-loaded components
const ProductCard = lazy(() => import("../components/ProductCard"));
const UserAnnouncementModal = lazy(() => import("../components/UserAnnouncementModal"));

// Preloaded home data
let preloadedProducts = [];
let preloadedClaimedIds = [];
let preloadedEligibleFree = [];
let preloadedShowPopup = false;

export const preloadHomeData = async (user) => {
  try {
    // Fetch all products
    const productSnap = await getDocs(collection(db, "products"));
    const allProducts = productSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    preloadedProducts = allProducts.filter((p) => p.price !== 0);

    if (!user) return;

    // Fetch user's claimed free products
    const purchasesSnap = await getDocs(
      query(collection(db, "purchases"), where("userId", "==", user.uid))
    );
    preloadedClaimedIds = purchasesSnap.docs
      .filter((d) => d.data().price === 0 && d.data().productId)
      .map((d) => d.data().productId);

    // Fetch user info
    const userSnap = await getDocs(query(collection(db, "users"), where("email", "==", user.email)));
    if (!userSnap.empty) {
      const userDoc = userSnap.docs[0];
      const rewardThreshold = userDoc.data().rewardThreshold ?? 50;

      // Count referrals
      const referralSnap = await getDocs(
        query(collection(db, "users"), where("referredBy", "==", user.uid))
      );
      const totalReferrals = referralSnap.docs.length;

      // Determine eligible free products
      const unclaimedFree = allProducts.filter(
        (p) => p.price === 0 && !preloadedClaimedIds.includes(p.id)
      );
      if (totalReferrals >= rewardThreshold && unclaimedFree.length > 0) {
        preloadedEligibleFree = unclaimedFree;
        preloadedShowPopup = true;
      }
    }
  } catch (err) {
    console.error("Preload error:", err);
  }
};

export default function Home() {
  const { user } = useAuth();
  const { darkMode } = useDarkMode();

  const [products, setProducts] = useState(preloadedProducts);
  const [claimedFreeIds, setClaimedFreeIds] = useState(preloadedClaimedIds);
  const [eligibleFreeProducts, setEligibleFreeProducts] = useState(preloadedEligibleFree);
  const [showPopup, setShowPopup] = useState(preloadedShowPopup);
  const [timeLeft, setTimeLeft] = useState(null);

  const updateTimeLeft = (expiryDate) => {
    const diff = expiryDate - new Date();
    if (diff <= 0) return setTimeLeft(null);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
  };

  useEffect(() => {
    let intervalId;

    // Fetch products if not preloaded
    if (!products.length) {
      preloadHomeData(user).then(() => {
        setProducts(preloadedProducts);
        setClaimedFreeIds(preloadedClaimedIds);
        setEligibleFreeProducts(preloadedEligibleFree);
        setShowPopup(preloadedShowPopup);
      });
    }

    // Discount timer
    const fetchTimer = async () => {
      try {
        const timerSnap = await getDocs(collection(db, "discountTimers"));
        if (!timerSnap.empty) {
          const { endTime } = timerSnap.docs[0].data();
          if (endTime?.toDate) {
            const expiryDate = endTime.toDate();
            updateTimeLeft(expiryDate);
            intervalId = setInterval(() => updateTimeLeft(expiryDate), 1000);
          }
        }
      } catch (err) {
        console.error("Failed to fetch discount timer:", err);
      }
    };
    fetchTimer();

    return () => clearInterval(intervalId);
  }, [user, products.length]);

  const handleFreeClaim = async (productId) => {
    if (!user) return toast.error("You must be logged in!");
    if (claimedFreeIds.includes(productId)) return toast.error("Already claimed!");

    try {
      setClaimedFreeIds((prev) => [...prev, productId]);

      const userSnap = await getDocs(query(collection(db, "users"), where("email", "==", user.email)));
      if (!userSnap.empty) {
        const userDoc = userSnap.docs[0];
        const userRef = doc(db, "users", userDoc.id);
        await updateDoc(userRef, { rewardThreshold: (userDoc.data().rewardThreshold ?? 50) + 50 });
        toast.success("🎉 Free product claimed!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to claim product");
    }
  };

  return (
    <div className={`min-h-screen py-6 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
      darkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-800"
    }`}>
      <Toaster />

      <Suspense fallback={null}>
        <UserAnnouncementModal />
      </Suspense>

      {timeLeft && (
        <div className="max-w-6xl mx-auto mb-6 bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100 p-4 rounded-lg text-center font-semibold">
          ⚡ Discounts active! Ends in: {timeLeft}
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-green-700 dark:text-green-400 mb-2">
          Available Farm Products
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Choose a product to invest in and earn daily income.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {products.map((product, i) => (
            <Suspense key={product.id} fallback={<div className="h-48 rounded shadow animate-pulse" />}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.3 }}
              >
                <ProductCard
                  product={product}
                  claimed={claimedFreeIds.includes(product.id)}
                  onClaim={() => handleFreeClaim(product.id)}
                />
              </motion.div>
            </Suspense>
          ))}
        </div>
      </div>

      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 text-black dark:text-white rounded-xl p-6 w-[90%] max-w-md shadow-xl text-center">
            <h3 className="text-xl font-semibold mb-2">🎁 Claim Your Free Product</h3>
            <p className="mb-4">You've reached your referral goal! Enjoy your free gift.</p>
            <div className="grid grid-cols-1 gap-4">
              {eligibleFreeProducts.map((product) => (
                <Suspense key={product.id} fallback={<div className="h-48 rounded shadow animate-pulse" />}>
                  <ProductCard
                    product={product}
                    claimed={claimedFreeIds.includes(product.id)}
                    onClaim={() => handleFreeClaim(product.id)}
                  />
                </Suspense>
              ))}
            </div>
            <button
              onClick={() => setShowPopup(false)}
              className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}